import store from '../store/index';
import { refreshToken } from '@/graphs/gql';
import { makeRequest } from '@/graphs/index';
import { guardRedirect, skipsTokenCheck } from './routeAccess';

interface JwtPayload {
  exp: number;
  [key: string]: any;
}

let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

export function checkIsAccessTokenExpired(): Promise<string | null> | void {
  let accessToken: string | null = null;
  let refreshTokenValue: string | null = null;

  try {
    accessToken = store.getters['users/getToken'];
    refreshTokenValue = store.getters['users/getRefreshToken'];
  } catch (e) {
    store.commit('users/SET_ME', null);
    store.commit('users/SET_IS_ME_SIGNED_IN', false);
  }

  if (accessToken) {
    const jwtPayload = parseJwt(accessToken);
    const expDif = Date.now() / 1000 - jwtPayload.exp;

    if (expDif >= 600) {
      store.commit('users/SET_ME', null);
      store.commit('users/SET_IS_ME_SIGNED_IN', false);
    } else if (expDif > -30 && expDif < 600) {
      if (refreshTokenValue) {
        if (isRefreshing) {
          return refreshPromise || Promise.resolve(null);
        }

        isRefreshing = true;
        refreshPromise = getNewAccessToken(refreshTokenValue)
          .then((newToken) => {
            if (newToken) {
              store.commit('users/SET_NEW_ACCESS_TOKEN', newToken);
              return newToken;
            }
            store.commit('users/SET_ME', null);
            store.commit('users/SET_IS_ME_SIGNED_IN', false);
            return null;
          })
          .catch((error) => {
            console.error('Error refreshing token:', error);
            store.commit('users/SET_ME', null);
            store.commit('users/SET_IS_ME_SIGNED_IN', false);
            return null;
          })
          .finally(() => {
            isRefreshing = false;
            refreshPromise = null;
          });

        return refreshPromise;
      }

      store.commit('users/SET_ME', null);
      store.commit('users/SET_IS_ME_SIGNED_IN', false);
    }
  } else {
    store.commit('users/SET_ME', null);
    store.commit('users/SET_IS_ME_SIGNED_IN', false);
  }
}

function parseJwt(token: string): JwtPayload {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(window.atob(base64).split('').map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`).join(''));

  return JSON.parse(jsonPayload);
}

async function getNewAccessToken(refreshTokenValue: string): Promise<string | null> {
  try {
    const response = await makeRequest('mutation', refreshToken, { token: refreshTokenValue }, null);
    if (response?.data?.refreshToken) {
      return response.data.refreshToken;
    }
    return null;
  } catch (error) {
    console.error('Error in getNewAccessToken:', error);
    return null;
  }
}

export { isOpenWhenSignedOut, isPublicRoute } from './routeAccess';

export default (to: any, from: any, next: any): void => {
  // Decided from the target route only: `window.location` is still the page being left.
  if (!skipsTokenCheck(to)) {
    checkIsAccessTokenExpired();
  }

  const redirectTo = guardRedirect(
    to,
    store.getters['users/isMeSignedIn'],
    store.getters['users/isAdmin'],
  );
  if (redirectTo) {
    next(redirectTo);
  } else {
    next();
  }
};
